import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

const isStrongUserPassword = (pwd) => {
  const minLength = 8;
  const hasUppercase = /[A-Z]/.test(pwd);
  const hasLowercase = /[a-z]/.test(pwd);
  const hasDigit = /[0-9]/.test(pwd);
  const hasSymbol = /[@#$%&]/.test(pwd);
  return pwd.length >= minLength && hasUppercase && hasLowercase && hasDigit && hasSymbol;
};

const ResetPasswordPage = ({ onNotify }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [invalidToken, setInvalidToken] = useState(false);

  useEffect(() => {
    if (!token) {
      setInvalidToken(true);
    }
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!password) {
      setError('Please enter a new password.');
      return;
    }

    if (!confirmPassword) {
      setError('Please confirm your password.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (!isStrongUserPassword(password)) {
      setError('Password must include 8+ chars, uppercase, lowercase, number, and one symbol from @ # $ % &.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          password,
          confirmPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 400) {
          setInvalidToken(true);
        }
        throw new Error(data.message || 'Failed to reset password. Please try again.');
      }

      setSubmitted(true);
      onNotify?.('Password reset successfully! Please log in with your new password.', 'success');
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
      onNotify?.(err.message || 'Failed to reset password. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (invalidToken && !submitted) {
    return (
      <section className="auth-page-shell">
        <div className="auth-card">
          <p className="auth-kicker">TK Fashion & Design</p>
          <h2>Invalid Reset Link</h2>
          <p className="auth-subtitle">
            The password reset link is missing or has expired. Please request a new one.
          </p>

          <div className="status-banner" style={{ marginBottom: '20px', textAlign: 'left' }}>
            ⚠ This link is no longer valid
          </div>

          <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
            <Link to="/forgot-password" className="checkout-btn" style={{ marginBottom: '10px', textAlign: 'center', textDecoration: 'none' }}>
              Request New Reset Link
            </Link>
            <Link to="/login" className="details-btn" style={{ textAlign: 'center', textDecoration: 'none' }}>
              Back to Login
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (submitted) {
    return (
      <section className="auth-page-shell">
        <div className="auth-card">
          <p className="auth-kicker">TK Fashion & Design</p>
          <h2>Password Reset Successful</h2>
          <p className="auth-subtitle">
            Your password has been successfully reset. You can now log in with your new password.
          </p>

          <div className="status-banner success" style={{ marginBottom: '20px', textAlign: 'left' }}>
            ✓ Password reset successfully
          </div>

          <button
            type="button"
            className="checkout-btn"
            onClick={() => navigate('/login')}
          >
            Go to Login
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="auth-page-shell">
      <div className="auth-card">
        <p className="auth-kicker">TK Fashion & Design</p>
        <h2>Reset Your Password</h2>
        <p className="auth-subtitle">
          Enter your new password below. Make sure it's strong and secure.
        </p>

        {error && <div className="status-banner">{error}</div>}

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <label>
            New Password
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="At least 8 chars with @ # $ % &"
              required
              disabled={submitting}
            />
          </label>

          <label>
            Confirm Password
            <input
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder="Re-enter password"
              required
              disabled={submitting}
            />
          </label>

          <p className="auth-password-hint">
            Password must include at least 8 characters, one uppercase letter, one lowercase letter,
            one number, and one symbol from @ # $ % &.
          </p>

          <button type="submit" className="checkout-btn" disabled={submitting}>
            {submitting ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <p className="auth-switch-text">
          Remember your password?{' '}
          <Link to="/login">
            Back to login
          </Link>
        </p>
      </div>
    </section>
  );
};

export default ResetPasswordPage;
